import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { getImportCodeStatus } from '../api/importCode';

interface ImportCodeModalProps {
  code: string;
  expiresAt: string; // RFC3339
  onClose: () => void;
  onImported?: () => void;
}

function computeSecondsLeft(expiresAt: string): number {
  const target = new Date(expiresAt).getTime();
  if (Number.isNaN(target)) {
    return 0;
  }
  return Math.max(0, Math.floor((target - Date.now()) / 1000));
}

function formatCountdown(seconds: number): string {
  const mm = Math.floor(seconds / 60).toString().padStart(2, '0');
  const ss = (seconds % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

// Selector for elements considered focusable when the focus trap wraps Tab/Shift-Tab.
const FOCUSABLE_SELECTOR =
  'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

const ImportCodeModal: React.FC<ImportCodeModalProps> = ({ code, expiresAt, onClose, onImported }) => {
  const { t } = useTranslation('common');
  const [secondsLeft, setSecondsLeft] = useState<number>(() => computeSecondsLeft(expiresAt));
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const copyResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Single interval for the modal's lifetime; recomputes from expiresAt to avoid drift.
  useEffect(() => {
    const tick = () => {
      setSecondsLeft(computeSecondsLeft(expiresAt));
    };
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [expiresAt]);

  const handleImported = useCallback(() => {
    if (onImported) onImported();
    onClose();
  }, [onImported, onClose]);

  useEffect(() => {
    const pollId = setInterval(async () => {
      if (computeSecondsLeft(expiresAt) <= 0) return;
      try {
        const status = await getImportCodeStatus(code);
        if (status.used) {
          clearInterval(pollId);
          handleImported();
        }
      } catch {
        // ignore poll errors
      }
    }, 3000);
    return () => clearInterval(pollId);
  }, [code, expiresAt, handleImported]);

  // Focus the primary action (close) on mount so keyboard users land inside the dialog.
  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  // Focus trap: keep Tab/Shift-Tab cycling within the modal body so background
  // page elements stay unreachable while aria-modal="true" is set on the overlay.
  // Modal.tsx supplies the dialog chrome (ESC, scroll-lock, ARIA) but does not
  // implement a focus trap, so we add one here.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const root = bodyRef.current;
      if (!root) return;
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Cleanup any pending copy-state reset timer on unmount.
  useEffect(() => {
    return () => {
      if (copyResetTimerRef.current !== null) {
        clearTimeout(copyResetTimerRef.current);
        copyResetTimerRef.current = null;
      }
    };
  }, []);

  const handleCopy = async () => {
    if (copyResetTimerRef.current !== null) {
      clearTimeout(copyResetTimerRef.current);
      copyResetTimerRef.current = null;
    }
    try {
      if (!navigator.clipboard || !navigator.clipboard.writeText) {
        throw new Error('clipboard API unavailable');
      }
      await navigator.clipboard.writeText(code);
      setCopyState('copied');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[ImportCodeModal] clipboard write failed', err);
      setCopyState('failed');
    }
    copyResetTimerRef.current = setTimeout(() => {
      setCopyState('idle');
      copyResetTimerRef.current = null;
    }, 2000);
  };

  const expired = secondsLeft <= 0;

  return (
    <Modal open={true} onClose={onClose} title={t('importCode.title')} width="420px">
      <div ref={bodyRef}>
        <p style={{ margin: '0 0 0.75rem 0', color: 'var(--text-2)', fontSize: '0.9rem' }}>
          {t('importCode.instruction')}
        </p>
        <div
          style={{
            fontFamily: 'monospace',
            fontSize: '1.6rem',
            letterSpacing: '0.2rem',
            textAlign: 'center',
            padding: '0.75rem 1rem',
            background: 'var(--bg-2, #f5f5f5)',
            border: '1px solid var(--border, #e0e0e0)',
            borderRadius: '6px',
            margin: '0 0 0.75rem 0',
            userSelect: 'all',
          }}
        >
          {code}
        </div>
        <p
          style={{
            margin: '0 0 1rem 0',
            color: expired ? 'var(--danger, #d33)' : 'var(--text-2)',
            fontSize: '0.9rem',
          }}
        >
          {expired
            ? t('importCode.expired')
            : t('importCode.timeRemaining', { time: formatCountdown(secondsLeft) })}
        </p>
        {copyState === 'copied' && (
          <p style={{ margin: '0 0 0.75rem 0', color: 'var(--primary, #0a84ff)', fontSize: '0.85rem' }}>
            {t('importCode.copied')}
          </p>
        )}
        {copyState === 'failed' && (
          <p style={{ margin: '0 0 0.75rem 0', color: 'var(--danger, #d33)', fontSize: '0.85rem' }}>
            {t('importCode.copyFailed')}
          </p>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCopy}
            disabled={expired}
          >
            {t('copy')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            ref={closeBtnRef}
            onClick={onClose}
          >
            {t('close')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ImportCodeModal;
